import React from "react";
import styled from "styled-components";
import { ScrollArea } from "@mantine/core";
import { ShiftDay } from "../../api/shiftDefinitionApi";
import { endMinutesOf } from "../../utils/shiftPattern";

/**
 * Haftalık program ızgarası — 7 gün × 24 saat (plan 2.24).
 *
 * Artık kendi verisini çekmiyor: doğrudan vardiya tanımının haftalık desenini
 * (`days`) alıyor. Eskiden ölü `/weekly-shift/:id` ucundan personel başına N
 * istek atıyordu.
 *
 * GECE VARDİYASI: 22:00–06:00 gibi bir vardiya tek satıra sığmaz. Bloğu
 * kırpmak yerine İKİ parça çiziliyor — o günün sonuna kadar, ve ertesi günün
 * başından bitişe kadar. Kırpmak gece vardiyasını yarım saat gösterirdi.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => `${i}:00`);
/** Izgara satırları JS gün numarasıyla (0 = Pazar) hizalı olmalı. */
const DAY_LABELS = ["PAZ", "PZT", "SAL", "ÇAR", "PER", "CU", "CTS"];

const DEFAULT_COLOR = "#C7D2FE";

type Block = { day: number; startHour: number; endHour: number; label: boolean };

/** Deseni ızgara bloklarına çevirir; gece yarısını aşan gün ikiye bölünür. */
const blocksOf = (days: ShiftDay[] | undefined): Block[] => {
  const blocks: Block[] = [];
  for (const day of days ?? []) {
    const startHour = day.startMinutes / 60;
    const endHour = endMinutesOf(day) / 60;

    if (endHour <= 24) {
      blocks.push({ day: day.dayOfWeek, startHour, endHour, label: true });
      continue;
    }
    blocks.push({ day: day.dayOfWeek, startHour, endHour: 24, label: true });
    blocks.push({
      day: (day.dayOfWeek + 1) % 7,
      startHour: 0,
      endHour: endHour - 24,
      label: false,
    });
  }
  return blocks;
};

type Props = {
  days: ShiftDay[] | undefined;
  shiftName?: string;
  color?: string;
};

const WeeklyShiftTable = ({ days, shiftName, color }: Props) => {
  const blocks = blocksOf(days);
  const background = color || DEFAULT_COLOR;

  return (
    <div>
      <HeaderContainer>
        <HeaderCellFirst />
        {HOURS.map((hour) => (
          <HeaderCellColumn key={hour}>{hour}</HeaderCellColumn>
        ))}
      </HeaderContainer>
      <ScrollArea>
        <ScheduleContainer>
          {DAY_LABELS.map((label, dayIndex) => (
            <React.Fragment key={label}>
              <HeaderCellRow>{label}</HeaderCellRow>
              {HOURS.map((_, hourIndex) => {
                const block = blocks.find(
                  (b) =>
                    b.day === dayIndex &&
                    hourIndex >= Math.floor(b.startHour) &&
                    hourIndex < Math.ceil(b.endHour)
                );
                if (!block) return <Cell key={`${dayIndex}-${hourIndex}`} />;

                const isFirst = hourIndex === Math.floor(block.startHour);
                const isLast = hourIndex === Math.ceil(block.endHour) - 1;
                const isMiddle =
                  hourIndex ===
                  Math.floor((block.startHour + block.endHour) / 2);

                return (
                  <Cell key={`${dayIndex}-${hourIndex}`}>
                    <ShiftBlock
                      $backgroundColor={background}
                      $isFirst={isFirst}
                      $isLast={isLast}
                    />
                    {block.label && isMiddle && shiftName && (
                      <BlockLabel>{shiftName}</BlockLabel>
                    )}
                  </Cell>
                );
              })}
            </React.Fragment>
          ))}
        </ScheduleContainer>
      </ScrollArea>
    </div>
  );
};

export default WeeklyShiftTable;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 100px repeat(24, 1fr);
  margin-bottom: 12px;
`;

const ScheduleContainer = styled.div`
  display: grid;
  grid-template-columns: 100px repeat(24, 1fr);
  grid-template-rows: repeat(7, 40px);
  text-align: center;
  position: relative;
`;

const HeaderCellColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #828282;
  transform: translateX(-50%);
`;

const HeaderCellRow = styled.div`
  height: 40px;
  display: flex;
  padding-left: 8px;
  align-items: center;
  border-left: 3px solid #5c26ff;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #828282;
  margin-bottom: 12px;
  background: #f9fafb;
`;

const HeaderCellFirst = styled.div``;

const Cell = styled.div`
  position: relative;
  margin-bottom: 12px;
  background: #f9fafb;
`;

const ShiftBlock = styled.div<{
  $isFirst?: boolean;
  $isLast?: boolean;
  $backgroundColor?: string;
}>`
  position: absolute;
  inset: 0;
  background: ${(props) => props.$backgroundColor};
  ${(props) => props.$isFirst && "border-radius: 5px 0 0 5px;"}
  ${(props) => props.$isLast && "border-radius: 0 5px 5px 0;"}
`;

const BlockLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  font-size: 12px;
  color: #1f2937;
  white-space: nowrap;
`;
